import { createRequest } from '@/services/request';

const request = createRequest();
const prefix = '/api/erp/v1';
const plmPrefix = '/api/plm/v1';

export type PageResult<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

export type PageQuery = { page?: number; pageSize?: number };

export type RemoteSalesOrderLine = {
  uuid: string;
  itemCode: string;
  itemName: string;
  productVersion: string;
  supplyType: 'PURCHASE' | 'PRODUCTION';
  quantity: string;
  unit: string;
  unitPrice: string;
  requiredDate: string;
};

export type RemoteSalesOrder = {
  uuid: string;
  orderCode: string;
  customerCode: string;
  customerName: string;
  currency: string;
  status: string;
  lines: RemoteSalesOrderLine[];
};

export type RemoteMrpRun = {
  uuid: string;
  runCode: string;
  status: string;
  inputSnapshotJson: {
    demandOrderUuids?: string[];
    demandOrderCodes?: string[];
    engineeringBaselineSelections?: Array<{
      salesOrderLineUuid: string;
      baselineCode: string;
      productCode: string;
      engineeringVersionCode: string;
    }>;
  };
  createdAt: string;
};

export type RemoteEngineeringBaseline = {
  uuid: string;
  baselineCode: string;
  releasePackageCode: string;
  productCode: string;
  productName: string;
  engineeringVersionCode: string;
  effectiveFrom: string;
  effectiveTo?: string;
  status: string;
  erpBomUuids: string[];
  processRequirements: Array<Record<string, unknown>>;
  qualityRequirements: Array<Record<string, unknown>>;
};

export type RemotePlmChangeImpact = {
  uuid: string;
  impactType: string;
  sourceSystem: string;
  objectCode: string;
  executionStage: string;
  status: string;
  sourceNote: string | null;
  disposition: string | null;
  dispositionNote: string | null;
};

export type RemotePlmChangeNotice = {
  uuid: string;
  changeCode: string;
  changeType: 'ECR' | 'ECN';
  productUuid: string;
  oldEngineeringVersionUuid: string | null;
  newEngineeringVersionUuid: string | null;
  changeReason: string;
  changeDescription: string | null;
  changeSource: string | null;
  priority: 'NORMAL' | 'URGENT';
  status: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  createdAt: string;
  updatedAt: string;
};

export type RemotePlmChangeDetail = RemotePlmChangeNotice & {
  impactObjects: RemotePlmChangeImpact[];
  executionReceipts: Array<{
    uuid: string;
    sourceSystem: string;
    objectCode: string;
    impactType: string;
    action: string;
    status: string;
    detail: string;
    occurredAt: string;
  }>;
};

export type RemoteSuggestion = {
  uuid: string;
  suggestionCode: string;
  mrpRunUuid: string;
  sourceSalesOrderUuid: string;
  suggestionType: 'PURCHASE' | 'PRODUCTION';
  itemCode: string;
  itemName: string;
  productVersion: string;
  quantity: string;
  unit: string;
  requiredDate: string;
  status: string;
};

export type RemotePurchaseRequisition = {
  uuid: string;
  requisitionCode: string;
  planningSuggestionUuid: string;
  mrpRunUuid: string;
  sourceSalesOrderUuid: string;
  status: string;
  sourcingSnapshot?: {
    sourceType?: 'WMS_COMPONENT_SHORTAGE';
    productionOrderCode?: string;
    warehouseUuid?: string;
    sourceSystem?: string;
    status?: string;
    submittedAt?: string;
    receivedAt?: string;
    rfqs?: Array<{
      rfqUuid: string;
      rfqCode: string;
      status: string;
      requisitionLineUuid: string;
    }>;
    scmRfqUuid?: string;
    scmRfqCode?: string;
    supplierCode?: string;
    supplierName?: string;
    unitPrice?: string;
    currencyCode?: string;
    promisedDate?: string;
    note?: string;
    error?: string;
  };
  lines: Array<RemoteSalesOrderLine & { uuid: string }>;
};

export type RemotePurchaseOrder = {
  uuid: string;
  purchaseOrderCode: string;
  supplierUuid: string;
  supplierCode: string;
  supplierName: string;
  expectedDate: string;
  status: string;
  wmsInboundTask?: {
    inboundOrderUuid: string;
    inboundOrderCode: string;
    warehouseUuid: string;
    status: string;
  };
  lines: Array<{
    uuid: string;
    purchaseRequisitionLineUuid?: string;
    itemCode: string;
    itemName: string;
    quantity: string;
    receivedQuantity: string;
    unit: string;
    requiredDate: string;
    receipts: Array<{ uuid: string; receivedQuantity: string; receivedAt: string; wmsInboundReference?: string }>;
  }>;
};

export type RemoteWmsWarehouse = {
  uuid: string;
  code: string;
  name: string;
  enabled: boolean;
};

export type RemoteWmsLocation = {
  uuid: string;
  warehouseUuid: string;
  code: string;
  zone: string;
  locationType: string;
  operationRule: string;
  enabled: boolean;
};

export type RemoteEamAssetProject = {
  lifecycleUuid: string;
  lifecycleNo: string;
  lifecycleName: string;
  lifecyclePhase: string;
  lifecycleStatus: string;
  ownerOrganizationUuid?: string;
  plannedCommissionAt?: string;
  actualCommissionAt?: string;
  asset?: { uuid: string; assetCode: string; name: string; status: string };
  references: Array<{
    referenceType: string;
    sourceSystem?: string;
    sourceDocumentNo?: string;
    sourceObjectId?: string;
    summary?: string;
  }>;
  updatedAt: string;
};

export type RemoteProductionOrder = {
  uuid: string;
  productionOrderCode: string;
  planningSuggestionUuid: string;
  sourceSalesOrderUuid: string;
  status: string;
  mesExecutionOrderUuid?: string | null;
  mesDispatchSnapshot?: {
    warehouseUuid?: string;
    receivingLocation?: string;
    routeCode?: string;
    routeVersion?: string;
    qmsProcessPlan?: { planCode?: string; planVersion?: string };
  };
  /** Read-only WMS physical-issue facts received through MES operation reports. */
  actualMaterialConsumption?: Array<{
    ledgerReference: string;
    reportUuid?: string;
    transactionType?: 'ISSUE' | 'RETURN';
    mesTaskUuid?: string;
    mesExecutionOrderUuid: string;
    wmsOutboundOrderUuid?: string;
    materialCode: string;
    quantity: number;
    lot: string;
    locationCode: string;
    balanceUuid: string;
    occurredAt: string;
  }>;
  componentReservation?: {
    wmsOutboundOrderUuid: string;
    wmsOutboundOrderCode: string;
    warehouseUuid: string;
    status: 'REQUEST_ACCEPTED' | 'RESERVED' | 'PREPARING' | 'MATERIAL_PREPARED' | 'MATERIAL_CLOSED' | 'SHORTAGE' | 'RELEASED';
    requestedAt: string;
    respondedAt: string | null;
    lines: Array<{ itemCode: string; itemName: string; requestedQuantity: number; reservedQuantity: number; exceptionCode?: string; exceptionMessage?: string }>;
  };
  shortageProcurement?: {
    purchaseRequisitionUuid: string;
    purchaseRequisitionCode: string;
    status: string;
    warehouseUuid?: string;
  };
  lines: Array<Pick<RemoteSalesOrderLine, 'itemCode' | 'itemName' | 'quantity' | 'unit' | 'requiredDate'>>;
  snapshot?: { productVersion: string; mbomVersion: string };
};

export type RemoteCustomerMaster = {
  uuid: string;
  customerCode: string;
  customerName: string;
  status: 'ACTIVE' | 'INACTIVE';
  sourceMode: 'ERP_MANAGED' | 'EXTERNAL_MANAGED';
  sourceSystem?: string;
};

export type RemoteItemMaster = {
  uuid: string;
  itemCode: string;
  itemName: string;
  unit: string;
  supplyType: 'PURCHASE' | 'PRODUCTION';
  productVersion: string;
  status: 'ACTIVE' | 'INACTIVE';
  sourceMode: 'ERP_MANAGED' | 'EXTERNAL_MANAGED';
  sourceSystem?: string;
};

export type RemoteSupplierMaster = {
  uuid: string;
  supplierCode: string;
  supplierName: string;
  status: 'ACTIVE' | 'INACTIVE';
  sourceMode: 'ERP_MANAGED' | 'EXTERNAL_MANAGED';
  sourceSystem?: string;
};

export type RemoteBomLine = {
  uuid: string;
  componentItemCode: string;
  componentItemName: string;
  componentProductVersion: string;
  quantity: string;
  unit: string;
};

export type RemoteBom = {
  uuid: string;
  bomCode: string;
  parentItemCode: string;
  parentItemName: string;
  productVersion: string;
  bomVersion: string;
  effectiveDate: string;
  status: 'DRAFT' | 'RELEASED' | 'RETIRED';
  sourceMode: 'ERP_MANAGED' | 'EXTERNAL_MANAGED';
  sourceSystem?: 'PLM';
  lines: RemoteBomLine[];
};

export type CreateSalesOrderPayload = {
  customerCode: string;
  customerName: string;
  lines: Array<{
    itemCode: string;
    itemName: string;
    productVersion: string;
    supplyType: 'PURCHASE' | 'PRODUCTION';
    quantity: string;
    unit: string;
    unitPrice: string;
    requiredDate: string;
  }>;
};

export type CreateWmsSalesOutboundPayload = {
  warehouseUuid: string;
};

export type ReleaseProductionOrderPayload = {
  warehouseUuid: string;
  receivingLocation?: string;
};

export type RemoteInventoryCostSummary = {
  source: 'ERP_WMS_MES';
  generatedAt: string;
  overview: {
    onHand: number;
    available: number;
    reserved: number;
    frozen: number;
    restricted: number;
    skuWarehouseCount: number;
    costStatus: 'PENDING_COSTING_LEDGER' | 'PROVISIONAL_MATERIAL_COST';
  };
  balances: Array<{
    id: string;
    sku: string;
    itemName: string;
    unit: string;
    warehouseUuid: string;
    warehouseCode: string;
    warehouseName: string;
    onHand: number;
    available: number;
    reserved: number;
    frozen: number;
    restricted: number;
    balanceCount: number;
    statuses: string[];
  }>;
  transactions: Array<{
    id: string;
    occurredAt: string;
    transactionType: string;
    referenceType: string;
    referenceUuid: string;
    warehouseCode: string;
    warehouseName: string;
    locationCode: string;
    sku: string;
    itemName: string;
    unit: string;
    lot: string;
    quantityDelta: number;
    onHandAfter: number;
    reservedAfter: number;
  }>;
  cost: {
    status: 'PENDING_COSTING_LEDGER' | 'PROVISIONAL_MATERIAL_COST';
    message: string;
    materialEvidence: {
      status: 'WMS_MATERIAL_FACTS_READY';
      message: string;
      productionOrderCount: number;
      issueFactCount: number;
      returnFactCount: number;
      materials: Array<{
        materialCode: string;
        itemName: string;
        unit: string;
        issuedQuantity: number;
        returnedQuantity: number;
        netConsumptionQuantity: number;
        issueFactCount: number;
        returnFactCount: number;
        productionOrderCodes: string[];
      }>;
    };
    provisionalMaterialCost: {
      status: 'PROVISIONAL_PURCHASE_PRICE';
      message: string;
      currencyCodes: string[];
      totalProvisionalAmount: number;
      valuedLineCount: number;
      unvaluedLineCount: number;
      materialLines: Array<{
        productionOrderCode: string;
        materialCode: string;
        itemName: string;
        unit: string;
        issuedQuantity: number;
        returnedQuantity: number;
        netConsumptionQuantity: number;
        weightedUnitPrice: number | null;
        currencyCode: string | null;
        provisionalAmount: number | null;
        valuationStatus: 'VALUED' | 'MISSING_PURCHASE_PRICE' | 'MULTI_CURRENCY_PRICE';
      }>;
    };
    variances: unknown[];
  };
};

export type RemoteFulfillmentSummary = {
  source: 'WMS';
  generatedAt: string;
  overview: {
    salesOrderCount: number;
    linkedOutboundOrderCount: number;
    shippedSalesOrderCount: number;
    qualityBlockedOrderCount: number;
  };
  orders: Array<{
    salesOrderUuid: string;
    salesOrderCode: string;
    customerCode: string;
    customerName: string;
    salesOrderStatus: string;
    orderedQuantity: number;
    shippedQuantity: number;
    overShippedQuantity: number;
    pendingQuantity: number;
    fulfillmentStatus: 'WAITING_FOR_WMS' | 'IN_PROGRESS' | 'QUALITY_BLOCKED' | 'SHIPPED';
    settlement: {
      shippedQuantity: string;
      signedQuantity: string;
      signedLines: Array<{ sku: string; quantity: number }>;
      signatureReference: string | null;
      signatureSource: 'LOGISTICS' | 'CRM_OMS' | null;
      signedAt: string | null;
      invoiceStatus: 'NOT_READY' | 'READY_TO_INVOICE' | 'INVOICED';
      invoiceNumber: string | null;
      invoicedAmount: string;
      invoicedAt: string | null;
      receivableStatus: 'NOT_READY' | 'PENDING_INVOICE' | 'OPEN' | 'SETTLED';
      settledAmount: string;
      settledAt: string | null;
    };
    lines: Array<{ sku: string; itemName: string; unit: string; orderedQuantity: number }>;
    outboundOrders: Array<{
      uuid: string;
      code: string;
      status: string;
      warehouseCode: string;
      warehouseName: string;
      totalQuantity: number;
      reservedQuantity: number;
      shippedQuantity: number;
      lines: Array<{ sku: string; name: string; quantity: number; reservedQty: number; lot?: string }>;
      erpAcknowledgedAt: string | null;
      shippedAt: string | null;
      inventorySupply: {
        status: 'REQUEST_ACCEPTED' | 'RESERVED' | 'SHORTAGE' | 'RELEASED';
        requestedAt: string;
        respondedAt: string | null;
        lines: Array<{
          sku: string;
          name: string;
          requestedQuantity: number;
          reservedQuantity: number;
          exceptionCode?: string;
          exceptionMessage?: string;
        }>;
      } | null;
    }>;
  }>;
};

export type RemoteFinanceSummary = {
  source: 'ERP_WMS';
  generatedAt: string;
  overview: {
    accountsReceivableCandidateCount: number;
    accountsReceivableCandidateAmount: number;
    accountsPayablePendingCount: number;
    accountsPayableQualityBlockedCount: number;
    readyVoucherCandidateCount: number;
    blockedVoucherCandidateCount: number;
    inventoryQuantity: number;
    costStatus: 'PROVISIONAL_MATERIAL_COST';
  };
  accountsReceivable: Array<{
    salesOrderUuid: string;
    salesOrderCode: string;
    customerCode: string;
    customerName: string;
    currency: string;
    shippedQuantity: number;
    signedQuantity: number;
    invoiceCandidateAmount: number;
    invoicedAmount: number;
    settledAmount: number;
    signatureReference: string | null;
    invoiceNumber: string | null;
    status: 'PENDING_SIGNATURE' | 'WAITING_FOR_SHIPMENT' | 'PENDING_INVOICE' | 'OPEN' | 'SETTLED';
    sourceOutboundOrderCount: number;
  }>;
  accountsPayable: Array<{
    inboundOrderUuid: string;
    inboundOrderCode: string;
    supplier: string;
    warehouseCode: string;
    receivedQuantity: number;
    status: 'PENDING_SUPPLIER_INVOICE' | 'QUALITY_BLOCKED' | 'WAITING_FOR_RECEIPT_OR_QUALITY';
    amount: null;
    amountStatus: 'PURCHASE_PRICE_NOT_CONNECTED';
  }>;
  voucherCandidates: Array<{
    candidateId: string;
    voucherType: 'AR_INVOICE' | 'AR_SETTLEMENT' | 'AP_ACCRUAL' | 'MATERIAL_CONSUMPTION';
    businessDocumentCode: string;
    sourceSystem: 'ERP' | 'ERP_WMS' | 'ERP_WMS_QMS' | 'ERP_MES_WMS';
    sourceDocumentCode: string;
    counterparty: string;
    currency: string;
    amount: number;
    status: 'READY_TO_SEND' | 'BLOCKED' | 'INFORMATION_ONLY';
    statusReason: string;
  }>;
  inventory: {
    quantity: number;
    balanceCount: number;
    status: 'QUANTITY_ONLY';
    message: string;
  };
};

export type RemoteAnalyticsSummary = {
  source: 'ERP_WMS';
  generatedAt: string;
  overview: {
    orderCount: number;
    openOrderCount: number;
    shippedOrderCount: number;
    orderValue: number;
    pendingFulfillmentQuantity: number;
    overShippedQuantity: number;
    qualityBlockedOrderCount: number;
    inventoryOnHand: number;
    inventoryAvailable: number;
    costStatus: 'PENDING_COSTING_LEDGER';
  };
  orderStageDistribution: Array<{ status: string; count: number }>;
  productionStatusDistribution: Array<{ status: string; count: number }>;
  orderRisks: Array<{
    salesOrderUuid: string;
    salesOrderCode: string;
    customerName: string;
    orderValue: number;
    status: string;
    fulfillmentStatus: string;
    pendingQuantity: number;
    overShippedQuantity: number;
  }>;
  inventoryBySku: Array<{
    sku: string;
    itemName: string;
    unit: string;
    onHand: number;
    available: number;
    restricted: number;
    warehouseName: string;
  }>;
};

export type RemoteGovernanceSummary = {
  source: 'ERP_WMS_QMS_MES';
  generatedAt: string;
  overview: { openExceptionCount: number; highSeverityExceptionCount: number; pendingApprovalCount: number; dataIssueCount: number };
  exceptions: Array<{
    id: string;
    category: string;
    severity: 'HIGH' | 'MEDIUM';
    title: string;
    objectCode: string;
    sourceSystem: string;
    ownerHint: string;
    status: 'OPEN';
  }>;
  approvals: Array<{ id: string; type: string; objectCode: string; detail: string; status: 'PENDING'; nextAction: string }>;
  dataBoundary: { message: string; pendingSources: string[] };
};

export type IntegrationTraceStatus = 'SUCCESS' | 'PROCESSING' | 'BLOCKED' | 'FAILED';
export type RemoteIntegrationTraceEvent = {
  id: string;
  sourceSystem: 'ERP' | 'WMS' | 'MES' | 'QMS';
  targetSystem: string;
  eventCode: string;
  eventName: string;
  status: IntegrationTraceStatus;
  detail: string;
  failureReason?: string;
  createdAt: string;
  completedAt?: string | null;
};
export type RemoteIntegrationTrace = {
  chainKey: string;
  workflow: string;
  businessCode: string;
  businessType: string;
  status: IntegrationTraceStatus;
  currentStep: string;
  blockerReason: string | null;
  updatedAt: string;
  events: RemoteIntegrationTraceEvent[];
};
export type RemoteIntegrationTraceResult = PageResult<RemoteIntegrationTrace> & {
  source: 'ERP_WMS_MES_QMS';
  generatedAt: string;
  overview: Record<'total' | 'processing' | 'blocked' | 'failed' | 'success', number>;
};
export type IntegrationTraceQuery = PageQuery & { keyword?: string; system?: 'ERP' | 'WMS' | 'MES' | 'QMS'; status?: IntegrationTraceStatus };

const idempotencyHeaders = () => ({
  'Idempotency-Key':
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `erp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
});

export const erpApi = {
  listSalesOrders: (params?: PageQuery) => request<PageResult<RemoteSalesOrder>>(`${prefix}/sales-orders`, { params }),
  listMrpRuns: (params?: PageQuery) => request<PageResult<RemoteMrpRun>>(`${prefix}/mrp-runs`, { params }),
  listSuggestions: (params?: PageQuery) => request<PageResult<RemoteSuggestion>>(`${prefix}/planning-suggestions`, { params }),
  listPurchaseRequisitions: (params?: PageQuery) => request<PageResult<RemotePurchaseRequisition>>(`${prefix}/purchase-requisitions`, { params }),
  listPurchaseOrders: (params?: PageQuery) => request<PageResult<RemotePurchaseOrder>>(`${prefix}/purchase-orders`, { params }),
  listWmsWarehouses: () => request<PageResult<RemoteWmsWarehouse>>('/api/wms/v1/warehouses', { params: { page: 1, pageSize: 100 } }),
  listWmsLocations: () => request<PageResult<RemoteWmsLocation>>('/api/wms/v1/locations', { params: { page: 1, pageSize: 100 } }),
  listAssetProjects: (params?: PageQuery) => request<PageResult<RemoteEamAssetProject>>(`${prefix}/asset-projects`, { params }),
  listProductionOrders: (params?: PageQuery) => request<PageResult<RemoteProductionOrder>>(`${prefix}/production-orders`, { params }),
  listCustomerMasters: (params?: PageQuery) => request<PageResult<RemoteCustomerMaster>>(`${prefix}/master-data/customers`, { params }),
  listItemMasters: (params?: PageQuery) => request<PageResult<RemoteItemMaster>>(`${prefix}/master-data/items`, { params }),
  listSupplierMasters: (params?: PageQuery) => request<PageResult<RemoteSupplierMaster>>(`${prefix}/master-data/suppliers`, { params }),
  listBoms: (params?: PageQuery) => request<PageResult<RemoteBom>>(`${prefix}/boms`, { params }),
  getInventoryCostSummary: () => request<RemoteInventoryCostSummary>(`${prefix}/inventory-cost/summary`),
  getFulfillmentSummary: () => request<RemoteFulfillmentSummary>(`${prefix}/fulfillment/summary`),
  getFinanceSummary: () => request<RemoteFinanceSummary>(`${prefix}/finance/summary`),
  getAnalyticsSummary: () => request<RemoteAnalyticsSummary>(`${prefix}/analytics/summary`),
  getGovernanceSummary: () => request<RemoteGovernanceSummary>(`${prefix}/governance/summary`),
  getIntegrationTrace: (params?: IntegrationTraceQuery) => request<RemoteIntegrationTraceResult>(`${prefix}/integration-trace`, { params }),
  createCustomerMaster: (payload: Pick<RemoteCustomerMaster, 'customerCode' | 'customerName'>) =>
    request(`${prefix}/master-data/customers`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  createItemMaster: (payload: Pick<RemoteItemMaster, 'itemCode' | 'itemName' | 'unit' | 'supplyType' | 'productVersion'>) =>
    request(`${prefix}/master-data/items`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  createSupplierMaster: (payload: Pick<RemoteSupplierMaster, 'supplierCode' | 'supplierName'>) =>
    request(`${prefix}/master-data/suppliers`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  createBom: (payload: { parentItemCode: string; bomVersion: string; effectiveDate: string; lines: Array<{ componentItemCode: string; quantity: string }> }) =>
    request(`${prefix}/boms`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  releaseBom: (uuid: string) => request(`${prefix}/boms/${uuid}/release`, { method: 'POST', headers: idempotencyHeaders() }),
  createSalesOrder: (payload: CreateSalesOrderPayload) =>
    request(`${prefix}/sales-orders`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  confirmSalesOrder: (uuid: string) =>
    request(`${prefix}/sales-orders/${uuid}/confirm`, { method: 'POST', headers: idempotencyHeaders() }),
  validateManufacturability: (uuid: string) =>
    request(`${prefix}/sales-orders/${uuid}/validate-manufacturability`, {
      method: 'POST',
      headers: idempotencyHeaders(),
    }),
  createWmsSalesOutbound: (uuid: string, payload: CreateWmsSalesOutboundPayload) =>
    request(`${prefix}/sales-orders/${uuid}/create-wms-outbound`, {
      method: 'POST',
      data: payload,
      headers: idempotencyHeaders(),
    }),
  confirmCustomerSignature: (uuid: string, payload: { signatureReference: string; signatureSource: 'LOGISTICS' | 'CRM_OMS'; signedQuantity?: string; lines?: Array<{ sku: string; quantity: string }>; signedAt?: string }) =>
    request(`${prefix}/sales-orders/${uuid}/customer-signature`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  createSalesInvoice: (uuid: string, payload: { invoiceNumber: string }) =>
    request(`${prefix}/sales-orders/${uuid}/invoices`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  settleSalesReceivable: (uuid: string, payload: { settledAmount: string; settledAt?: string }) =>
    request(`${prefix}/sales-orders/${uuid}/receivables/settle`, { method: 'POST', data: payload, headers: idempotencyHeaders() }),
  listEngineeringBaselines: (params?: PageQuery) => request<PageResult<RemoteEngineeringBaseline>>(`${prefix}/engineering-baselines`, { params }),
  createMrpRun: (demandOrderUuids: string[], engineeringBaselineSelections: Array<{ salesOrderLineUuid: string; engineeringBaselineUuid: string }>) =>
    request<RemoteMrpRun>(`${prefix}/mrp-runs`, {
      method: 'POST',
      data: { demandOrderUuids, engineeringBaselineSelections },
      headers: idempotencyHeaders(),
    }),
  approveSuggestion: (uuid: string) =>
    request(`${prefix}/planning-suggestions/${uuid}/approve`, {
      method: 'POST',
      headers: idempotencyHeaders(),
    }),
  convertToPurchaseRequisition: (uuid: string) =>
    request(`${prefix}/planning-suggestions/${uuid}/convert-to-purchase-requisition`, {
      method: 'POST',
      headers: idempotencyHeaders(),
    }),
  submitPurchaseRequisitionToSrm: (uuid: string) =>
    request<RemotePurchaseRequisition>(`${prefix}/purchase-requisitions/${uuid}/submit-to-srm`, {
      method: 'POST',
      headers: idempotencyHeaders(),
    }),
  convertToProductionOrder: (uuid: string) =>
    request(`${prefix}/planning-suggestions/${uuid}/convert-to-production-order`, {
      method: 'POST',
      headers: idempotencyHeaders(),
    }),
  createPurchaseOrder: (payload: {
    supplierCode: string;
    expectedDate: string;
    lines: Array<{ itemCode: string; quantity: string; purchaseRequisitionLineUuid?: string; requiredDate?: string }>;
  }) => request<RemotePurchaseOrder>(`${prefix}/purchase-orders`, {
    method: 'POST',
    data: payload,
    headers: idempotencyHeaders(),
  }),
  createWmsPurchaseInbound: (purchaseOrderUuid: string, warehouseUuid: string) =>
    request<{ inboundOrderUuid: string; inboundOrderCode: string; status: string }>(
      `${prefix}/purchase-orders/${purchaseOrderUuid}/wms-inbound`,
      { method: 'POST', data: { warehouseUuid }, headers: idempotencyHeaders() },
    ),
  linkPurchaseOrderLineToEam: (
    purchaseOrderUuid: string,
    payload: { purchaseOrderLineUuid: string; eamCategoryId?: number },
  ) => request<{ link: RemoteEamAssetProject }>(`${prefix}/purchase-orders/${purchaseOrderUuid}/eam-assets`, {
    method: 'POST',
    data: payload,
    headers: idempotencyHeaders(),
  }),
  releaseProductionOrderToMes: (uuid: string, payload: ReleaseProductionOrderPayload) =>
    request(`${prefix}/production-orders/${uuid}/release-to-mes`, {
      method: 'POST',
      data: payload,
    headers: idempotencyHeaders(),
  }),
  requestProductionComponentReservation: (uuid: string, payload: { warehouseUuid: string }) =>
    request(`${prefix}/production-orders/${uuid}/request-component-reservation`, {
      method: 'POST', data: payload, headers: idempotencyHeaders(),
    }),
};

export const plmChangeApi = {
  listChangeNotices: () => request<PageResult<RemotePlmChangeNotice>>(`${plmPrefix}/change-notices?page=1&pageSize=100`),
  getChangeNotice: (uuid: string) => request<RemotePlmChangeDetail>(`${plmPrefix}/change-notices/${uuid}`),
};
